import React, { useState, useEffect, useCallback, useMemo } from 'react';
import DOMPurify from 'dompurify';
import { Shield, Eye, FileText, Download, X, Check, ExternalLink, BarChart3, Calendar, Tag } from 'lucide-react';
import { API_URL } from '../config';

// ============ COULEURS & ICONES PAR TYPE DE DOCUMENT ============
const TYPE_META = {
  reglement_interieur: {
    label: 'Règlement intérieur',
    icon: Shield,
    bg: 'bg-indigo-100', text: 'text-indigo-700', border: 'border-indigo-500',
    accentBg: 'bg-indigo-50 dark:bg-indigo-950/40', accentBorder: 'border-indigo-200 dark:border-indigo-800'
  },
  notice: {
    label: 'Notice individuelle',
    icon: Eye,
    bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-500',
    accentBg: 'bg-amber-50 dark:bg-amber-950/40', accentBorder: 'border-amber-200 dark:border-amber-800'
  },
  note_service: {
    label: 'Note de service',
    icon: FileText,
    bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-500',
    accentBg: 'bg-blue-50 dark:bg-blue-950/40', accentBorder: 'border-blue-200 dark:border-blue-800'
  }
};

// Configuration DOMPurify : autoriser uniquement les balises de mise en forme issues de mammoth
const SANITIZE_CONFIG = {
  ALLOWED_TAGS: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'br', 'ul', 'ol', 'li', 'strong', 'em', 'b', 'i', 'u', 'table', 'thead', 'tbody', 'tr', 'td', 'th', 'a', 'span', 'div'],
  ALLOWED_ATTR: ['href', 'class', 'colspan', 'rowspan'],
  ALLOW_DATA_ATTR: false
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
  } catch { return iso; }
};

const formatDateTime = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

const DocumentsReglementaires = ({ authUser, authToken }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [docContent, setDocContent] = useState(null);
  const [loadingContent, setLoadingContent] = useState(false);
  const [showAuditPanel, setShowAuditPanel] = useState(false);

  const isAdmin = ['Patron', 'Super Manager'].includes(authUser?.role_name);

  const loadDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/documents-reglementaires`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      const data = await res.json();
      setDocuments(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Erreur chargement documents:', e);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  useEffect(() => { loadDocuments(); }, [loadDocuments]);

  // ========= OUVERTURE MODALE DE CONSULTATION =========
  const openDocument = async (doc) => {
    setSelectedDoc(doc);
    setLoadingContent(true);
    setDocContent(null);
    try {
      const res = await fetch(`${API_URL}/documents-reglementaires/${doc.id}`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      const data = await res.json();
      setDocContent(data);
      loadDocuments();
    } catch (e) {
      console.error('Erreur chargement contenu:', e);
    } finally {
      setLoadingContent(false);
    }
  };

  const closeModal = () => {
    setSelectedDoc(null);
    setDocContent(null);
  };

  // Fermeture par Escape
  useEffect(() => {
    if (!selectedDoc) return;
    const handler = (e) => { if (e.key === 'Escape') closeModal(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [selectedDoc]);

  // HTML sanitizé pour affichage sécurisé (mammoth produit du HTML interne mais on défend en profondeur)
  const sanitizedHtml = useMemo(() => {
    if (!docContent?.contenu_html) return '<p>Contenu non disponible.</p>';
    return DOMPurify.sanitize(docContent.contenu_html, SANITIZE_CONFIG);
  }, [docContent]);

  // ========= TELECHARGEMENT PDF =========
  const downloadPdf = async (doc) => {
    try {
      const res = await fetch(`${API_URL}/documents-reglementaires/${doc.id}/download`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      if (!res.ok) throw new Error('Téléchargement impossible');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${doc.code}_${doc.titre.replace(/[^a-zA-Z0-9._-]/g, '_')}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      loadDocuments();
    } catch (e) {
      console.error('Erreur téléchargement:', e);
      alert('Erreur lors du téléchargement du PDF');
    }
  };

  // ========= AUDIT MATRIX (Patron/SM) =========
  const [auditMatrix, setAuditMatrix] = useState([]);
  const [loadingAudit, setLoadingAudit] = useState(false);

  const loadAuditMatrix = useCallback(async () => {
    if (!isAdmin) return;
    setLoadingAudit(true);
    try {
      const res = await fetch(`${API_URL}/documents-reglementaires-audit/matrix`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      const data = await res.json();
      setAuditMatrix(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Erreur audit:', e);
      setAuditMatrix([]);
    } finally {
      setLoadingAudit(false);
    }
  }, [authToken, isAdmin]);

  useEffect(() => {
    if (showAuditPanel) loadAuditMatrix();
  }, [showAuditPanel, loadAuditMatrix]);

  // Exporter la matrice en CSV
  const exportAuditCsv = () => {
    if (!auditMatrix.length) return;
    const rows = [['Nom', 'Prénom', 'Email', 'Service', 'Rôle', 'Document', 'Dernière consultation', 'Téléchargé']];
    auditMatrix.forEach(r => {
      rows.push([
        r.last_name || '',
        r.first_name || '',
        r.email || '',
        r.service_name || '',
        r.role_name || '',
        `${r.document_code} — ${r.document_titre}`,
        r.derniere_consultation ? formatDateTime(r.derniere_consultation) : 'Jamais',
        r.a_telecharge ? 'Oui' : 'Non'
      ]);
    });
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(';')).join('\n');
    const blob = new Blob(["﻿" + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-documents-reglementaires-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Grouper la matrice par employé pour affichage
  const matrixByEmployee = {};
  auditMatrix.forEach(r => {
    const key = r.employee_id;
    if (!matrixByEmployee[key]) {
      matrixByEmployee[key] = {
        employee_id: r.employee_id,
        first_name: r.first_name, last_name: r.last_name, email: r.email,
        service_name: r.service_name, role_name: r.role_name,
        documents: {}
      };
    }
    matrixByEmployee[key].documents[r.document_code] = {
      titre: r.document_titre,
      derniere_consultation: r.derniere_consultation,
      a_telecharge: r.a_telecharge
    };
  });
  const docCodes = [...new Set(auditMatrix.map(r => r.document_code))];

  return (
    <div className="bg-white rounded-xl shadow-lg p-3 sm:p-6">
      {/* ============ EN-TÊTE ============ */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 sm:mb-6">
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="bg-indigo-100 dark:bg-indigo-900/40 p-2 sm:p-3 rounded-xl">
            <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div>
            <h2 className="text-base sm:text-xl font-bold text-gray-800">Documents RH &amp; Réglementation</h2>
            <p className="text-xs sm:text-sm text-gray-500">Règlement intérieur, notices et notes de service applicables — Best Events SAS</p>
          </div>
        </div>
        {isAdmin && (
          <button
            onClick={() => setShowAuditPanel(!showAuditPanel)}
            className={`px-3 py-2 rounded-lg text-xs sm:text-sm font-medium transition flex items-center justify-center gap-2 ${
              showAuditPanel
                ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
            aria-label="Afficher le tableau de traçabilité des consultations"
          >
            <BarChart3 className="w-4 h-4" />
            {showAuditPanel ? 'Voir les documents' : 'Tableau de traçabilité'}
          </button>
        )}
      </div>

      {/* ============ BANDEAU LÉGAL ============ */}
      {!showAuditPanel && (
        <div className="bg-indigo-50 dark:bg-indigo-950/40 border-l-4 border-indigo-500 dark:border-indigo-500 p-3 sm:p-4 rounded-r-lg mb-5">
          <p className="text-xs sm:text-sm text-gray-700">
            Ces documents sont mis à votre disposition en application des articles <strong>L1321-4 et R1321-1 du Code du travail</strong>,
            de l'article <strong>L1222-4 du Code du travail</strong> et des articles <strong>12 à 14 du RGPD</strong>.
            Ils sont accessibles à l'ensemble du personnel sans condition. Toute consultation et tout téléchargement sont enregistrés
            à des fins de preuve d'accessibilité.
          </p>
        </div>
      )}

      {/* ============ VUE DOCUMENTS ============ */}
      {!showAuditPanel && (
        <>
          {loading ? (
            <div className="text-center py-12 text-gray-500">Chargement des documents...</div>
          ) : documents.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              Aucun document publié pour le moment.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {documents.map(doc => {
                const meta = TYPE_META[doc.type] || TYPE_META.note_service;
                const Icon = meta.icon;
                const consulted = !!doc.derniere_consultation;
                return (
                  <article
                    key={doc.id}
                    className={`border-2 ${meta.accentBorder} ${meta.accentBg} rounded-xl p-4 sm:p-5 flex flex-col gap-3 hover:shadow-md transition`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${meta.bg} ${meta.text}`}>
                        <Icon className="w-3.5 h-3.5" aria-hidden="true" />
                        {meta.label}
                      </span>
                      <span className="text-xs text-gray-500 font-mono flex items-center gap-1">
                        <Tag className="w-3 h-3" aria-hidden="true" />
                        {doc.version}
                      </span>
                    </div>

                    <h3 className="text-base sm:text-lg font-bold text-gray-800 leading-snug">
                      {doc.titre}
                    </h3>

                    <div className="text-xs text-gray-600 space-y-0.5">
                      <p className="flex items-center gap-1.5">
                        <Calendar className="w-3.5 h-3.5 text-gray-400" aria-hidden="true" />
                        <span>Applicable depuis le <strong>{formatDate(doc.date_application)}</strong></span>
                      </p>
                      <p className="text-gray-500 pl-5">Publié le {formatDate(doc.date_publication)}</p>
                    </div>

                    {doc.description && (
                      <p className="text-sm text-gray-700 leading-relaxed line-clamp-4">
                        {doc.description}
                      </p>
                    )}

                    {consulted ? (
                      <div className="flex items-center gap-1.5 text-xs text-green-700 bg-green-50 dark:bg-green-950/40 border border-green-200 dark:border-green-800 rounded-md px-2 py-1.5">
                        <Check className="w-3.5 h-3.5" aria-hidden="true" />
                        <span>Consulté le {formatDateTime(doc.derniere_consultation)}</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded-md px-2 py-1.5">
                        <span aria-hidden="true">⚠</span>
                        <span>Non encore consulté</span>
                      </div>
                    )}

                    <div className="flex flex-col sm:flex-row gap-2 mt-auto pt-2">
                      <button
                        onClick={() => openDocument(doc)}
                        className="flex-1 px-3 py-2 min-h-[44px] bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition flex items-center justify-center gap-2"
                        aria-label={`Consulter ${doc.titre} en ligne`}
                      >
                        <ExternalLink className="w-4 h-4" aria-hidden="true" />
                        Consulter
                      </button>
                      <button
                        onClick={() => downloadPdf(doc)}
                        className="flex-1 px-3 py-2 min-h-[44px] bg-white border-2 border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition flex items-center justify-center gap-2"
                        aria-label={`Télécharger ${doc.titre} au format PDF`}
                      >
                        <Download className="w-4 h-4" aria-hidden="true" />
                        Télécharger PDF
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ============ VUE AUDIT (Patron/SM) ============ */}
      {showAuditPanel && isAdmin && (
        <div>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <div>
              <h3 className="text-lg font-bold text-gray-800">Tableau de traçabilité des consultations</h3>
              <p className="text-xs text-gray-500">Preuve d'accessibilité des documents pour chaque salarié — utilisable en cas de contentieux prud'homal.</p>
            </div>
            <button
              onClick={exportAuditCsv}
              disabled={!auditMatrix.length}
              className="px-3 py-2 min-h-[44px] bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium flex items-center justify-center gap-2"
              aria-label="Exporter la matrice de consultation au format CSV"
            >
              <Download className="w-4 h-4" aria-hidden="true" />
              Export CSV
            </button>
          </div>

          {loadingAudit ? (
            <div className="text-center py-8 text-gray-500">Chargement de la matrice...</div>
          ) : auditMatrix.length === 0 ? (
            <div className="text-center py-8 text-gray-500">Aucune donnée à afficher.</div>
          ) : (
            <div className="overflow-x-auto border border-gray-200 rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold text-gray-700">Employé</th>
                    <th className="text-left px-3 py-2 font-semibold text-gray-700 hidden md:table-cell">Service</th>
                    <th className="text-left px-3 py-2 font-semibold text-gray-700 hidden md:table-cell">Rôle</th>
                    {docCodes.map(code => (
                      <th key={code} className="text-center px-3 py-2 font-semibold text-gray-700">{code}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Object.values(matrixByEmployee).map(emp => (
                    <tr key={emp.employee_id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="px-3 py-2">
                        <div className="font-medium text-gray-800">{emp.last_name} {emp.first_name}</div>
                        <div className="text-xs text-gray-500">{emp.email}</div>
                      </td>
                      <td className="px-3 py-2 hidden md:table-cell text-gray-600">{emp.service_name || '—'}</td>
                      <td className="px-3 py-2 hidden md:table-cell text-gray-600">{emp.role_name || '—'}</td>
                      {docCodes.map(code => {
                        const cell = emp.documents[code];
                        const consulted = cell && cell.derniere_consultation;
                        return (
                          <td key={code} className="px-3 py-2 text-center">
                            {consulted ? (
                              <span className="inline-flex flex-col items-center gap-0.5" title={cell.a_telecharge ? 'Document téléchargé' : 'Consulté'}>
                                <Check className={`w-4 h-4 ${cell.a_telecharge ? 'text-blue-600 dark:text-blue-400' : 'text-green-600 dark:text-green-400'}`} aria-hidden="true" />
                                <span className="text-[10px] text-gray-500">{formatDateTime(cell.derniere_consultation)}</span>
                              </span>
                            ) : (
                              <span className="text-amber-600 dark:text-amber-400 text-xs font-medium" aria-label="Non consulté">—</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="mt-3 flex items-center gap-4 text-xs text-gray-600">
            <span className="inline-flex items-center gap-1"><Check className="w-3.5 h-3.5 text-green-600 dark:text-green-400" aria-hidden="true" /> Consulté</span>
            <span className="inline-flex items-center gap-1"><Check className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" aria-hidden="true" /> Téléchargé</span>
            <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400">— Non consulté</span>
          </div>
        </div>
      )}

      {/* ============ MODALE DE CONSULTATION ============ */}
      {selectedDoc && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-2 sm:p-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="doc-modal-title"
          onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
        >
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[95vh] flex flex-col">
            <div className="flex items-center justify-between gap-3 p-4 border-b border-gray-200 bg-white rounded-t-xl">
              <div className="flex-1 min-w-0">
                <h3 id="doc-modal-title" className="text-base sm:text-lg font-bold text-gray-800 truncate">{selectedDoc.titre}</h3>
                <p className="text-xs text-gray-500">{selectedDoc.code} — {selectedDoc.version} — Applicable depuis le {formatDate(selectedDoc.date_application)}</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => downloadPdf(selectedDoc)}
                  className="px-3 py-2 min-h-[44px] bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-xs sm:text-sm font-medium flex items-center gap-1.5"
                  aria-label="Télécharger le PDF de ce document"
                >
                  <Download className="w-4 h-4" aria-hidden="true" />
                  <span className="hidden sm:inline">Télécharger PDF</span>
                </button>
                <button
                  onClick={closeModal}
                  className="p-2 min-h-[44px] min-w-[44px] text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition"
                  aria-label="Fermer"
                >
                  <X className="w-5 h-5" aria-hidden="true" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 sm:p-6">
              {loadingContent ? (
                <div className="text-center py-12 text-gray-500">Chargement du document...</div>
              ) : !docContent ? (
                <div className="text-center py-12 text-gray-500">Erreur de chargement</div>
              ) : (
                <article
                  className="prose prose-sm sm:prose max-w-none doc-reglementaire-content"
                  dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
                />
              )}
            </div>

            <div className="border-t border-gray-200 p-3 bg-gray-50 rounded-b-xl flex flex-col sm:flex-row justify-between items-center gap-2 text-xs text-gray-600">
              <span>Consultation enregistrée à {formatDateTime(new Date().toISOString())}</span>
              <span className="text-gray-400">Echap pour fermer</span>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .doc-reglementaire-content h1 { color: #1f2937; font-weight: 700; margin-top: 1.5rem; margin-bottom: 0.75rem; padding-bottom: 0.25rem; border-bottom: 2px solid #e5e7eb; font-size: 1.5rem; }
        .doc-reglementaire-content h2 { color: #1f2937; font-weight: 700; margin-top: 1.25rem; margin-bottom: 0.5rem; padding-bottom: 0.2rem; border-bottom: 2px solid #e5e7eb; font-size: 1.25rem; }
        .doc-reglementaire-content h3 { color: #4f46e5; font-weight: 600; margin-top: 1rem; margin-bottom: 0.5rem; font-size: 1.125rem; }
        .doc-reglementaire-content p { margin: 0.5rem 0; line-height: 1.6; }
        .doc-reglementaire-content ul, .doc-reglementaire-content ol { margin: 0.5rem 0 0.5rem 1.5rem; }
        .doc-reglementaire-content li { margin: 0.25rem 0; line-height: 1.5; }
        .doc-reglementaire-content table { border-collapse: collapse; margin: 1rem 0; }
        .doc-reglementaire-content td, .doc-reglementaire-content th { border: 1px solid #d1d5db; padding: 0.5rem; }
        .doc-reglementaire-content strong { font-weight: 600; }
      `}</style>
    </div>
  );
};

export default DocumentsReglementaires;
